import { createApi } from "@reduxjs/toolkit/query/react";
import {
  IUserInfo,
  IUserResponse,
  ILogOutResponse,
  IGetClientsResponse,
  IGetClientsSearchParams,
  IGetDriversResponse,
  ICreateDriverResponse,
  IGetDriverResponse,
  IUploadDriverPhoto,
  IUpdateDriverCommission,
  IGetNewsLettersResponse,
  ICreateNotificationCampaign,
  ICreateNotificationCampaignResponse,
  IGetDiscountSettings,
  ICreateDiscountForClient,
  IGetClientDiscountsResponse,
  IGetTaxiServiceParamsResponse,
  IGetBookingsResponse,
  IGetServerInfoResponse,
  IBookingDetails,
  IClientInfoForOrder,
  IFindPlacesResponse,
  IFindPlacesRequest,
  IFindPlace,
  IPreEstimateBookingResponse,
  IPreEstimateBookingRequest,
  IEstimateBookingResponse,
  IBookingsSearchParams,
  IBookingsResponse,
  IUpdateDriver,
  IUploadNotificationImageResponse,
  IAddClientBonusRequest,
  IClientBonusesResponse,
  IGetBookingStatisticResponse,
} from "./types";
import { baseQueryConfig } from "./baseQueryConfig";
import { IClient } from "pages/ClientsPage/types";
import { INotification } from "pages/OrdersPage/components/Notifications/types";
import INewsLetter from "pages/NewsletterPage/types";
import { IClientOrder } from "pages/ClientsPage/components/ClientOrders/types";

const atixApi = createApi({
  reducerPath: "atixApi",
  baseQuery: baseQueryConfig,
  tagTypes: [
    "Driver",
    "NewsLetters",
    "Discounts",
    "GetDiscounts",
    "Tariffs",
    "AutoProcess",
    "Orders",
    "LeaveReview",
    "BonusRides",
    "OperatorComment",
    "HideOrder",
    "NoShow",
    "FailBooking",
    "RejectBooking",
    "SwitchRequestCarDetails",
    "NotificationDone",
    "CreateOrder",
    "ChangeOrderCost",
    "UpdateDriverPhoto",
  ],
  endpoints: (builder) => ({
    getUserInfo: builder.mutation<IUserResponse, IUserInfo>({
      query: (userInfo) => ({
        url: "/api/operator/current/login",
        method: "POST",
        body: JSON.stringify(userInfo),
      }),
    }),
    logOut: builder.mutation<ILogOutResponse, void>({
      query: () => ({
        url: "/api/operator/current/logout",
        method: "POST",
      }),
    }),
    getClients: builder.mutation<
      IGetClientsResponse,
      { page: number } & IGetClientsSearchParams
    >({
      query: ({ page, ...searchParams }) => ({
        url: `/api/operator/client/list?page=${page}`,
        method: "POST",
        body: JSON.stringify(searchParams),
      }),
    }),
    updateClientComment: builder.mutation<
      void,
      { phone: string } & IGetClientsSearchParams
    >({
      query: ({ phone, ...searchParams }) => ({
        url: `/api/operator/client/updateComment/${phone}`,
        method: "POST",
        body: JSON.stringify(searchParams),
      }),
      invalidatesTags: ["OperatorComment"],
    }),
    blockClient: builder.mutation<IClient, { phone: string }>({
      query: ({ phone }) => ({
        url: `/api/operator/client/set/status/fraud/${phone}`,
        method: "POST",
      }),
    }),
    activateClient: builder.mutation<IClient, { phone: string }>({
      query: ({ phone }) => ({
        url: `/api/operator/client/set/status/open/${phone}`,
        method: "POST",
      }),
    }),
    resetClientPinCounters: builder.mutation<IClient, { phone: string }>({
      query: ({ phone }) => ({
        url: `/api/operator/operator/resetClientPintCounters/${phone}`,
        method: "POST",
      }),
    }),
    getClientBookings: builder.query<
      IClientOrder[],
      { phone: string; page: number }
    >({
      query: ({ phone, page }) => ({
        url: `/api/operator/client/bookings/${phone}/${page}`,
      }),
    }),
    getClientBonuses: builder.query<
      IClientBonusesResponse,
      { phone: string; page: number }
    >({
      query: ({ phone, page }) => ({
        url: `/api/operator/client/${phone}/bonus?page=${page}`,
      }),
    }),
    addClientBonus: builder.mutation<void, IAddClientBonusRequest>({
      query: ({ phone, bonus }) => ({
        url: `/api/operator/client/${phone}/bonus`,
        method: "POST",
        body: JSON.stringify(bonus),
      }),
    }),
    incrementClientCredit: builder.mutation<
      number,
      { phone: string; credit: string; id: { bookingId: string } }
    >({
      query: ({ phone, credit, id }) => ({
        url: `/api/operator/client/${phone}/credit/${credit}`,
        method: "PUT",
        body: JSON.stringify(id),
      }),
    }),
    getDrivers: builder.mutation<
      IGetDriversResponse,
      { page: number } & IGetClientsSearchParams
    >({
      query: ({ page, ...searchParams }) => ({
        url: `/api/operator/driver/list?page=${page}`,
        method: "POST",
        body: JSON.stringify(searchParams),
      }),
    }),
    createDriver: builder.mutation<ICreateDriverResponse, FormData>({
      query: (driverData) => ({
        url: "/api/operator/driver",
        method: "POST",
        body: driverData,
      }),
    }),

    getDriver: builder.query<IGetDriverResponse, string>({
      query: (id) => ({
        url: `/api/operator/driver/details?driverId=${id}`,
      }),
      providesTags: ["Driver", "UpdateDriverPhoto"],
      keepUnusedDataFor: 0,
    }),
    updateDriverCommission: builder.mutation<void, IUpdateDriverCommission>({
      query: (driverCommissionInfo) => ({
        url: "/api/operator/driver/commission",
        method: "POST",
        body: JSON.stringify(driverCommissionInfo),
      }),
      invalidatesTags: ["Driver"],
    }),
    deleteDriver: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/operator/driver/${id}`,
        method: "DELETE",
      }),
    }),
    resetDriverPassword: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/operator/driver/resetPassword?driverId=${id}`,
        method: "POST",
      }),
    }),
    updateDriver: builder.mutation<ICreateDriverResponse, IUpdateDriver>({
      query: (driverInfo) => ({
        url: "/api/operator/driver",
        method: "PUT",
        body: JSON.stringify(driverInfo),
      }),
    }),
    uploadDriverPhoto: builder.mutation<string, IUploadDriverPhoto>({
      query: ({ driverId, type, photo }) => ({
        url: `/api/operator/driver/photo/${type}/${driverId}`,
        method: "POST",
        body: photo,
      }),
      invalidatesTags: ["UpdateDriverPhoto"],
    }),
    getNewsLetters: builder.query<IGetNewsLettersResponse, number>({
      query: (page) => ({
        url: `/api/operator/clientNotificationCampaign/list?page=${page}`,
      }),
      providesTags: ["NewsLetters"],
    }),
    createNotificationCampaign: builder.mutation<
      ICreateNotificationCampaignResponse,
      ICreateNotificationCampaign
    >({
      query: (notificationInfo) => ({
        url: "/api/operator/clientNotificationCampaign",
        method: "POST",
        body: JSON.stringify(notificationInfo),
      }),
      invalidatesTags: ["NewsLetters"],
    }),
    uploadNotificationImage: builder.mutation<
      IUploadNotificationImageResponse,
      FormData
    >({
      query: (file) => ({
        url: "/api/operator/upload/image",
        method: "POST",
        body: file,
      }),
    }),
    updateNotificationCampaign: builder.mutation<
      ICreateNotificationCampaignResponse,
      ICreateNotificationCampaign
    >({
      query: (notificationInfo) => ({
        url: `/api/operator/clientNotificationCampaign`,
        method: "PUT",
        body: JSON.stringify(notificationInfo),
      }),
      invalidatesTags: ["NewsLetters"],
    }),
    getNotificationCampaign: builder.query<INewsLetter, string>({
      query: (campaignId) => ({
        url: `/api/operator/clientNotificationCampaign/${campaignId}`,
      }),
    }),
    runNotificationCampaign: builder.mutation<
      void,
      { campaignId: string; isSupervisorsOnly: boolean }
    >({
      query: ({ campaignId, isSupervisorsOnly }) => ({
        url: `/api/operator/clientNotificationCampaign/${
          isSupervisorsOnly
            ? "send_notification_campaign_to_supervisors"
            : "send_notification_campaign_to_all"
        }/${campaignId}`,
        method: "POST",
      }),
    }),
    stopNotificationCampaign: builder.mutation<void, string>({
      query: (campaignId) => ({
        url: `/api/operator/clientNotificationCampaign/stop/${campaignId}`,
        method: "POST",
      }),
    }),
    removeNotificationCampaign: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/operator/clientNotificationCampaign/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["NewsLetters"],
    }),
    getDiscountSettings: builder.query<IGetDiscountSettings, void>({
      query: () => ({
        url: "/api/operator/discounts/settings",
      }),
      providesTags: ["Discounts"],
    }),
    updateDiscountSettings: builder.mutation<void, IGetDiscountSettings>({
      query: (settings) => ({
        url: "/api/operator/discounts/settings",
        method: "PUT",
        body: JSON.stringify(settings),
      }),
      invalidatesTags: ["Discounts"],
    }),
    createDiscountForClient: builder.mutation<
      void,
      { phone: string } & ICreateDiscountForClient
    >({
      query: ({ phone, ...discountInfo }) => ({
        url: `/api/operator/discounts/${phone}`,
        method: "POST",
        body: JSON.stringify(discountInfo),
      }),
      invalidatesTags: ["GetDiscounts"],
    }),
    getClientDiscounts: builder.query<
      IGetClientDiscountsResponse,
      { phone: string; page: number }
    >({
      query: ({ phone, page }) => ({
        url: "/api/operator/discounts",
        params: { phone, page },
      }),
      providesTags: ["GetDiscounts"],
    }),
    deleteDiscount: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/operator/discounts/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Discounts"],
    }),
    getTaxiServiceParams: builder.query<IGetTaxiServiceParamsResponse, void>({
      query: () => ({
        url: "/api/operator/taxiService/1",
      }),
      providesTags: ["Tariffs"],
    }),
    setTaxiServiceParams: builder.mutation<void, IGetTaxiServiceParamsResponse>(
      {
        query: (params) => ({
          url: "/api/operator/taxiService",
          method: "POST",
          body: JSON.stringify(params),
        }),
        invalidatesTags: ["Tariffs"],
      },
    ),
    getBookings: builder.query<IGetBookingsResponse, void>({
      query: () => ({
        url: "/api/operator/bookings",
      }),
      providesTags: [
        "Orders",
        "HideOrder",
        "NoShow",
        "FailBooking",
        "RejectBooking",
        "SwitchRequestCarDetails",
        "NotificationDone",
        "CreateOrder",
        "ChangeOrderCost",
      ],
    }),
    getServerInfo: builder.query<IGetServerInfoResponse, void>({
      query: () => ({
        url: "/api/operator/serverInfo",
      }),
      providesTags: ["AutoProcess"],
    }),
    postBookingAutoProcessing: builder.mutation<void, boolean>({
      query: (payload) => ({
        url: `/api/operator/booking/auto/process/${payload}`,
        method: "POST",
      }),
      invalidatesTags: ["AutoProcess"],
    }),
    checkOperatorBookingCreated: builder.mutation<void, string>({
      query: (bookingId) => ({
        url: `/api/operator/checkOperatorBookingCreated/${bookingId}`,
        method: "POST",
      }),
      invalidatesTags: ["Orders"],
    }),
    getBookingDetails: builder.query<IBookingDetails, string>({
      query: (bookingId) => ({
        url: `/api/operator/booking/details/${bookingId}`,
      }),
      providesTags: [
        "OperatorComment",
        "BonusRides",
        "LeaveReview",
        "HideOrder",
      ],
    }),
    resetBonusRides: builder.mutation<void, { phone: string; id: string }>({
      query: ({ phone, id }) => ({
        url: `/api/operator/resetClientBonusRides/${phone}/${id}`,
        method: "PUT",
      }),
      invalidatesTags: ["BonusRides"],
    }),
    askedToLeaveReview: builder.mutation<void, { phone: string; id: string }>({
      query: ({ phone, id }) => ({
        url: `/api/operator/askedToLeaveReviewCommand/${phone}/${id}`,
        method: "POST",
      }),
      invalidatesTags: ["LeaveReview"],
    }),
    sendBookingSms: builder.mutation<
      void,
      {
        clientDriverParam: "client" | "driver";
        params: { bookingId: string; text: string };
      }
    >({
      query: ({ clientDriverParam, params }) => ({
        url: `/api/operator/bookingSms/${clientDriverParam}`,
        method: "POST",
        body: JSON.stringify(params),
      }),
    }),
    sendBookingDetailsToClient: builder.mutation<void, { bookingId: string }>({
      query: ({ bookingId }) => ({
        url: `/api/operator/driverDetailsSms/${bookingId}`,
        method: "POST",
      }),
    }),
    updateBookingPhone: builder.mutation<void, string>({
      query: (bookingId) => ({
        url: `/api/operator/booking/${bookingId}/phone/update`,
        method: "POST",
      }),
    }),
    hideBooking: builder.mutation<void, string>({
      query: (bookingId) => ({
        url: `/api/booking/${bookingId}/hide`,
        method: "PUT",
      }),
      invalidatesTags: ["HideOrder"],
    }),
    sendBooking: builder.mutation<void, string>({
      query: (bookingId) => ({
        url: `/api/operator/send/booking/${bookingId}`,
        method: "POST",
      }),
    }),
    requestCarDetails: builder.query<void, string>({
      query: (bookingId) => ({
        url: `/api/operator/booking/request/car/details/${bookingId}`,
      }),
    }),
    notShowBooking: builder.mutation<void, string>({
      query: (bookingId) => ({
        url: `/api/booking/${bookingId}/noshow`,
        method: "PUT",
      }),
      invalidatesTags: ["NoShow"],
    }),
    failBooking: builder.mutation<void, string>({
      query: (bookingId) => ({
        url: `/api/booking/${bookingId}/fail`,
        method: "PUT",
      }),
      invalidatesTags: ["FailBooking"],
    }),
    rejectBooking: builder.mutation<void, string>({
      query: (bookingId) => ({
        url: `/api/booking/${bookingId}/reject`,
        method: "PUT",
      }),
      invalidatesTags: ["RejectBooking"],
    }),
    switchRequestCarDetails: builder.mutation<
      void,
      { bookingId: string; stop: boolean }
    >({
      query: ({ bookingId, stop }) => ({
        url: `/api/operator/switchCarDetailsRequest/${stop ? "stop" : "start"}`,
        method: "POST",
        body: JSON.stringify({ bookingId: bookingId }),
      }),
      invalidatesTags: ["SwitchRequestCarDetails"],
    }),
    restoreBooking: builder.mutation<void, string>({
      query: (bookingId) => ({
        url: `/api/booking/${bookingId}/restore`,
        method: "PUT",
      }),
    }),
    getBookingHistory: builder.query<void, string>({
      query: (bookingId) => ({
        url: `/api/operator/booking/history/${bookingId}`,
      }),
    }),
    sendNotificationDone: builder.mutation<INotification, string>({
      query: (notificationId) => ({
        url: `/api/operator/notification/done/${notificationId}`,
        method: "POST",
      }),
      invalidatesTags: ["NotificationDone"],
    }),
    getClientInfo: builder.query<IClientInfoForOrder, string>({
      query: (phone) => ({
        url: `/api/operator/client/info/${phone}`,
      }),
    }),
    findPlaces: builder.mutation<IFindPlacesResponse, IFindPlacesRequest>({
      query: ({ city, textToSearch, loadAllFlag }) => ({
        url: `/api/operator/places/by/text/${city}?lang=ru`,
        method: "POST",
        body: JSON.stringify({ text: textToSearch, loadAll: loadAllFlag }),
      }),
    }),
    getPlaceDetails: builder.query<
      IFindPlace,
      { placeId: string; city: string }
    >({
      query: ({ placeId, city }) => ({
        url: `/api/operator/place/details/${placeId}?city=${city}`,
      }),
    }),
    estimateBooking: builder.mutation<
      IEstimateBookingResponse,
      IPreEstimateBookingRequest
    >({
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      async queryFn(booking, { dispatch, getState }, extraOptions, baseQuery) {
        try {
          const initialResponse = await baseQuery({
            url: "/api/json/uk/booking/preestimate",
            method: "POST",
            body: JSON.stringify(booking),
          });

          if (initialResponse.error) {
            console.error(initialResponse.error);
          }

          const initialData =
            initialResponse.data as IPreEstimateBookingResponse;

          // If we have a bookingId, start polling for the full estimation result
          if (initialData.bookingId) {
            return new Promise<{ data: IEstimateBookingResponse }>(
              (resolve, reject) => {
                const startTime = Date.now();
                const TIMEOUT = 10000;
                const INTERVAL = 1000;

                const pollInterval = setInterval(async () => {
                  if (Date.now() - startTime > TIMEOUT) {
                    clearInterval(pollInterval);
                    reject(new Error("Estimation timeout"));
                    return;
                  }

                  try {
                    const pollResponse = await baseQuery(
                      `/api/json/uk/booking/preestimate/${initialData.bookingId}`,
                    );

                    const estimationResult =
                      pollResponse.data as IEstimateBookingResponse;
                    if (estimationResult.cost?.standard) {
                      clearInterval(pollInterval);
                      resolve({ data: estimationResult });
                    }
                  } catch (error) {
                    clearInterval(pollInterval);
                    reject(error);
                  }
                }, INTERVAL);
              },
            );
          }

          throw new Error("Invalid response format: missing bookingId");
        } catch (error) {
          return { error: error as { data: unknown; status: number } };
        }
      },
    }),
    createBooking: builder.mutation<
      { bookingId: string },
      IPreEstimateBookingRequest
    >({
      query: (booking) => ({
        url: "/api/operator/booking",
        method: "POST",
        body: JSON.stringify(booking),
      }),
      invalidatesTags: ["CreateOrder"],
    }),
    saveOperatorCost: builder.mutation<
      void,
      { bookingId: string; operatorCost: string }
    >({
      query: ({ bookingId, operatorCost }) => ({
        url: `/api/operator/set/booking/cost/${bookingId}/${operatorCost}`,
        method: "POST",
      }),
      invalidatesTags: ["ChangeOrderCost"],
    }),
    findBookings: builder.query<IBookingsResponse, IBookingsSearchParams>({
      query: (params) => ({
        url: "api/operator/find/bookings",
        params: params,
      }),
    }),
    getBookingStatistic: builder.query<
      IGetBookingStatisticResponse,
      { from: number; to: number }
    >({
      query: (params) => ({
        url: "api/operator/report",
        params: params,
      }),
    }),
  }),
});

export const {
  useGetUserInfoMutation,
  useLogOutMutation,
  useGetClientsMutation,
  useUpdateClientCommentMutation,
  useBlockClientMutation,
  useActivateClientMutation,
  useResetClientPinCountersMutation,
  useGetClientBookingsQuery,
  useGetClientBonusesQuery,
  useAddClientBonusMutation,
  useIncrementClientCreditMutation,
  useGetDriversMutation,
  useCreateDriverMutation,
  useGetDriverQuery,
  useUpdateDriverCommissionMutation,
  useDeleteDriverMutation,
  useResetDriverPasswordMutation,
  useUpdateDriverMutation,
  useUploadDriverPhotoMutation,
  useGetNewsLettersQuery,
  useCreateNotificationCampaignMutation,
  useUploadNotificationImageMutation,
  useUpdateNotificationCampaignMutation,
  useGetNotificationCampaignQuery,
  useRunNotificationCampaignMutation,
  useStopNotificationCampaignMutation,
  useRemoveNotificationCampaignMutation,
  useGetDiscountSettingsQuery,
  useUpdateDiscountSettingsMutation,
  useCreateDiscountForClientMutation,
  useGetClientDiscountsQuery,
  useDeleteDiscountMutation,
  useGetTaxiServiceParamsQuery,
  useSetTaxiServiceParamsMutation,
  useGetBookingsQuery,
  useGetServerInfoQuery,
  usePostBookingAutoProcessingMutation,
  useCheckOperatorBookingCreatedMutation,
  useGetBookingDetailsQuery,
  useResetBonusRidesMutation,
  useAskedToLeaveReviewMutation,
  useSendBookingSmsMutation,
  useSendBookingDetailsToClientMutation,
  useUpdateBookingPhoneMutation,
  useHideBookingMutation,
  useSendBookingMutation,
  useRequestCarDetailsQuery,
  useNotShowBookingMutation,
  useFailBookingMutation,
  useRejectBookingMutation,
  useSwitchRequestCarDetailsMutation,
  useRestoreBookingMutation,
  useGetBookingHistoryQuery,
  useSendNotificationDoneMutation,
  useGetClientInfoQuery,
  useFindPlacesMutation,
  useGetPlaceDetailsQuery,
  useEstimateBookingMutation,
  useCreateBookingMutation,
  useSaveOperatorCostMutation,
  useFindBookingsQuery,
  useGetBookingStatisticQuery,
} = atixApi;

export default atixApi;
